import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import type { TourStep } from '../../lib/tour'
import { placeCoachmark, type CoachmarkPlacement } from '../../lib/notePlacement'

// The tour's one card (lib/tour.ts, hooks/useTour.ts): a lime outline round
// the real control it is about, the rest of the screen dimmed, and an ink card
// beside it, the look of InfoButton's note. Nothing is blocked: the control
// works as ever (doing a do step is how it moves on), and so does everything
// else. While the control isn't on screen (another tab) or a sheet is open, it
// waits out of sight. Skip tour, or Escape, ends the tour.

const PAD = 6
const POLL_MS = 250

interface Spot { top: number; left: number; width: number; height: number; card: CoachmarkPlacement }

const same = (a: Spot | null, b: Spot | null) => a === b || (!!a && !!b
  && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height)

function measure(anchor: string): Spot | null {
  if (document.querySelector('[role="dialog"]')) return null
  const el = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (!r.width && !r.height) return null
  const viewport = { width: window.innerWidth, height: window.innerHeight }
  return {
    top: r.top - PAD, left: r.left - PAD, width: r.width + 2 * PAD, height: r.height + 2 * PAD,
    card: placeCoachmark({ top: r.top - PAD, bottom: r.bottom + PAD, left: r.left, width: r.width }, viewport),
  }
}

export function Coachmark({ step, number, total, onNext, onSkip }: {
  step: TourStep
  number: number
  total: number
  onNext: () => void
  onSkip: () => void
}) {
  // Measured as it opens (App mounts one per step), then kept up to date.
  const [spot, setSpot] = useState<Spot | null>(() => measure(step.anchor))
  const update = useCallback(() => {
    const next = measure(step.anchor)
    setSpot(prev => (same(prev, next) ? prev : next))
  }, [step.anchor])

  // The control may come and go (a tab change, a list that loads), and move
  // (a scroll, the keyboard): looked for again a few times a second.
  useEffect(() => {
    const timer = setInterval(update, POLL_MS)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, { passive: true, capture: true })
    return () => {
      clearInterval(timer)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, { capture: true })
    }
  }, [update])

  // A step whose control is below the fold brings it into view, once.
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`)
    const r = el?.getBoundingClientRect()
    if (el && r && (r.top < 0 || r.bottom > window.innerHeight)) el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  }, [step.anchor])

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onSkip() }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [onSkip])

  if (!spot) return null
  const { card } = spot
  const cardStyle: Record<string, string> = {
    left: `${card.left}px`, width: `${card.width}px`, '--ml-info-arrow': `${card.arrow}px`,
    ...(card.side === 'below' ? { top: `${card.top}px` } : { bottom: `${card.bottom}px` }),
  }

  return createPortal(
    <div className="ml-coach">
      <div data-testid="coachmark-spot" className={`ml-coach__spot${step.kind === 'do' ? ' is-do' : ''}`} aria-hidden="true"
        style={{ top: `${spot.top}px`, left: `${spot.left}px`, width: `${spot.width}px`, height: `${spot.height}px` }} />
      <section key={step.id} className={`ml-coach__card is-${card.side}`} style={cardStyle as CSSProperties} aria-label="Tour" aria-live="polite">
        <span className="ml-coach__count ml-num">{number} of {total}</span>
        <p className="ml-coach__text">{step.text}</p>
        <div className="ml-coach__actions">
          <button type="button" className="ml-coach__skip" onClick={onSkip}>Skip tour</button>
          {step.kind === 'look' && <button type="button" className="ml-info__ok" onClick={onNext}>Got it</button>}
        </div>
      </section>
    </div>,
    document.body,
  )
}
